import { Anchor, Radio, Stack } from '@vtex/admin-ui'
import { RadioGroup } from '@vtex/admin-ui-form'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { MarketingTags, OPERATORS } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from '../../../Drawer'
import { MarketingTagsSelector } from '../../Selectors'
import { messages } from './messages'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: MarketingTags | undefined
  onSave: (config: MarketingTags | undefined) => void
}

const EMPTY_CONFIG: MarketingTags = Object.freeze({
  operator: OPERATORS.EQUALS,
  tags: [],
})

export function MarketingTagsEditor(props: Props) {
  const { state, initialConfig, onSave } = props
  const { hide } = state

  const { $t } = useIntl()

  const isCreating = initialConfig === undefined

  const config = useMemo(
    () => initialConfig ?? { ...EMPTY_CONFIG },
    [initialConfig]
  )

  const { drawerForm } = useDrawerSubForm<MarketingTags>({
    config,
    isOpen: state.open,
  })

  const handleSave = async () => {
    const values = drawerForm.getValues()
    onSave(values.tags.length > 0 ? values : undefined)
    hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>
          {$t(messages.editor.description, {
            a: (content: any) => (
              <Anchor
                target="_blank"
                href={$t(messages.editor.moreInformation)}
              >
                {content}
              </Anchor>
            ),
          })}
        </DrawerSubtitle>

        <DismissButton onHide={hide} />
      </DrawerHeader>
      <DrawerContent>
        <Stack space="$space-3" fluid>
          <RadioGroup
            name="operator"
            state={drawerForm}
            label={null}
            direction="row"
          >
            {[OPERATORS.EQUALS, OPERATORS.DIFFERENT].map((value) => (
              <Radio
                key={value}
                label={$t(messages.editor[value])}
                value={value}
              />
            ))}
          </RadioGroup>
          <MarketingTagsSelector name="tags" state={drawerForm} />
        </Stack>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
